import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Close,
  CloudDownloadOutlined,
  Delete,
  InsertPhoto,
  AttachFile,
} from "@mui/icons-material";
import { useFileService } from "../../../../hooks/api/files";
import { FileResponseDto } from "../../../../hooks/api/files/interfaces/fileResponse.dto";
import { useAlert } from "../../../../hooks/alert";
import { downloadBase64File } from "../../utils/downloadBase64File";

interface FilesPanelProps {
  appointmentId: number;
  onClose: () => void;
  isDesktop: boolean;
  windowWidth: number;
  windowHeight: number;
}

const isImageFile = (mimeType: string) => {
  return mimeType.startsWith("image/");
};

export const FilesPanel: React.FC<FilesPanelProps> = ({
  appointmentId,
  onClose,
  isDesktop,
  windowWidth,
  windowHeight,
}) => {
  const [files, setFiles] = useState<FileResponseDto[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { postCreate, getFiles, getFile, deleteFile } = useFileService();
  const { error: showError } = useAlert();
  const getFilesRef = useRef(getFiles);
  const showErrorRef = useRef(showError);
  getFilesRef.current = getFiles;
  showErrorRef.current = showError;

  const fetchFiles = useCallback(async () => {
    try {
      const data = await getFilesRef.current(appointmentId);
      if (data) {
        setFiles(data);
      }
    } catch (err) {
      showErrorRef.current("Erro ao carregar arquivos");
    }
  }, [appointmentId]);

  useEffect(() => {
    fetchFiles();
  }, [appointmentId, fetchFiles]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    const formData = new FormData();
    formData.append("url", file.name);
    formData.append("appointmentId", String(appointmentId));
    formData.append("file", file);

    try {
      await postCreate({ body: formData });
      await fetchFiles();
    } catch (err) {
      showError("Erro ao enviar arquivo");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDownloadFile = async (file: FileResponseDto) => {
    try {
      const fileData = await getFile(file.id);
      downloadBase64File(fileData, file.mime_type, file.display_name);
    } catch (err) {
      showError("Erro ao baixar arquivo");
    }
  };

  const handleDeleteFile = async (fileId: number) => {
    try {
      await deleteFile(fileId);
      await fetchFiles();
    } catch (err) {
      showError("Erro ao excluir arquivo");
    }
  };

  const panelWidth = isDesktop ? Math.min(400, windowWidth * 0.3) : windowWidth;
  const panelHeight = isDesktop ? windowHeight : windowHeight * 0.85;

  const panelStyle: React.CSSProperties = isDesktop
    ? {
        position: "absolute",
        top: 0,
        right: 0,
        width: panelWidth,
        height: panelHeight,
        backgroundColor: "#fff",
        borderLeft: "1px solid #E2E8F0",
        display: "flex",
        flexDirection: "column",
        zIndex: 200,
      }
    : {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        zIndex: 200,
      };

  return (
    <>
      <Box sx={panelStyle}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #E2E8F0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Arquivos
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Files List */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          {files.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "text.secondary",
              }}
            >
              <AttachFile sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant="body2">
                Nenhum arquivo compartilhado
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => {
                  const isImage = isImageFile(file.mime_type);

                  return (
                    <TableRow key={file.id}>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",
                          }}
                          onClick={() => handleDownloadFile(file)}
                        >
                          {isImage ? (
                            <InsertPhoto
                              fontSize="small"
                              sx={{ color: "#0A6172" }}
                            />
                          ) : (
                            <AttachFile
                              fontSize="small"
                              sx={{ color: "#64748B" }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 180,
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {file.display_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadFile(file)}
                        >
                          <CloudDownloadOutlined fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteFile(file.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Box>

        {/* Upload Button */}
        <Box sx={{ p: 2, borderTop: "1px solid #E2E8F0" }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: "none" }}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          />
          <Button
            variant="contained"
            fullWidth
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            sx={{
              backgroundColor: "#0A6172",
              "&:hover": { backgroundColor: "#085461" },
              borderRadius: 3,
              py: 1.5,
            }}
          >
            {isUploading ? (
              <CircularProgress size={24} sx={{ color: "#fff" }} />
            ) : (
              "ENVIAR DOCUMENTO"
            )}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default FilesPanel;
